// One question as a teacher sets it — shared by the paper editor and the
// Author page, so a question written into a paper and one written into the
// bank cannot drift apart: they are the same component.
//
// Per question: type and marks; a time, defaulting to the publish rule; on a
// subjective question a word limit; a live maths preview;
// TEAM (Tips, Explanation, Answer with its marking scheme, common Mistakes),
// which AI can fill or rewrite part by part and which stays editable; the
// third-attempt video; and a concept tag that points at a real sub-topic.
import { useMemo, useState, type ReactNode } from 'react';
import {
  TEAM_PART_LABEL,
  TEAM_PARTS,
  blankTeamParts,
  defaultAllocatedSeconds,
  isEnabledQuestionType,
  schemeTotal,
  wordCount,
  wordLimitFor,
  youTubeEmbedUrl,
} from '@cwy/config/contracts';
import type { ConceptOption, CustomPaperItem, TeamPart } from '@/types';
import { MathText } from '@/components/exam/MathText';
import { QUESTION_TYPE_LABEL, QUESTION_TYPES, type PaperQuestionType } from './editorItems';

const LABEL = 'mb-1 block text-fs-11 font-semibold uppercase tracking-wide';
const INPUT =
  'w-full rounded-xl border border-line px-3 py-2 text-sm outline-none focus:border-forest disabled:bg-paper-sunk';
const SMALL_NUMBER =
  'w-14 rounded-lg border border-line px-2 py-1.5 text-center text-xs outline-none focus:border-forest disabled:bg-paper-sunk';

export type PatchItem = (index: number, patch: Partial<CustomPaperItem>, part?: TeamPart) => void;

/** A live render of `$…$` maths, shown only when there is some to render. */
export function MathPreview({ text }: { text: string }) {
  if (!text.includes('$')) return null;
  return (
    <div className="mt-1.5 rounded-lg bg-paper-sunk px-3 py-2 text-sm text-ink">
      <span className="mr-2 text-fs-10 font-bold uppercase tracking-wide text-slate-400">
        Preview
      </span>
      <MathText>{text}</MathText>
    </div>
  );
}

export function PaperItemEditor({
  idPrefix,
  item,
  concepts,
  editable,
  busy,
  generating,
  tagging,
  onPatch,
  onTypeChange,
  onGenerate,
  onTag,
  onRemove,
  lockKey = false,
  lockShape = false,
  retagLabel = '↻ Re-tag',
}: {
  /** Keeps radio-group names unique when several editors share a page. */
  idPrefix: string;
  item: CustomPaperItem;
  concepts: ConceptOption[];
  editable: boolean;
  busy: boolean;
  generating: boolean;
  tagging: boolean;
  onPatch: PatchItem;
  onTypeChange: (index: number, type: PaperQuestionType) => void;
  onGenerate: (parts: TeamPart[], mode: 'fill' | 'rewrite') => void;
  onTag: () => void;
  /** Absent where there is nothing to remove the question from — the Author page. */
  onRemove?: () => void;
  /**
   * Hold the question, its options, the marked choice and the Answer part
   * still while the help stays editable — a published paper's key, which
   * students may already have been marked against.
   */
  lockKey?: boolean;
  /** Hold type, marks, time and word limit still — fixed once a paper is published. */
  lockShape?: boolean;
  /** The re-tag button's words, where tagging is the model's rather than the word-match. */
  retagLabel?: string;
}) {
  const type = item.type ?? 'SHORT_DESC';
  const marks = item.marks ?? 1;
  const options = item.options?.length ? item.options : type === 'MCQ' ? ['', '', '', ''] : [];
  const locked = !editable || busy;
  const keyLocked = locked || lockKey;
  const shapeLocked = locked || lockShape;

  const defaultSeconds = defaultAllocatedSeconds(type, marks);
  const seconds = item.allocatedSeconds ?? defaultSeconds;
  const limit = wordLimitFor({ type, wordLimit: item.wordLimit });

  return (
    <div className="rounded-2xl border border-line bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-sm font-bold text-ink">Q{item.index}</span>
        <select
          value={type}
          disabled={shapeLocked}
          onChange={(event) => onTypeChange(item.index, event.target.value as PaperQuestionType)}
          className="rounded-lg border border-line bg-card px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-forest disabled:bg-paper-sunk"
        >
          {QUESTION_TYPES.map((questionType) => (
            <option key={questionType.value} value={questionType.value}>
              {questionType.label}
            </option>
          ))}
          {/* Keep an unsupported imported type visible until the teacher changes it. */}
          {!isEnabledQuestionType(type) && (
            <option value={type} disabled>
              {QUESTION_TYPE_LABEL[type]} — not offered
            </option>
          )}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-500">
          Marks
          <input
            type="number"
            min={0.5}
            step={0.5}
            value={item.marks ?? ''}
            disabled={shapeLocked}
            onChange={(event) =>
              onPatch(item.index, {
                marks: event.target.value === '' ? null : Number(event.target.value),
              })
            }
            className={SMALL_NUMBER}
          />
        </label>

        {/* The default is the publish rule itself, so leaving it alone changes nothing. */}
        <label className="flex items-center gap-1 text-xs text-slate-500">
          Time
          <input
            type="number"
            min={0.5}
            step={0.5}
            value={seconds / 60}
            disabled={shapeLocked}
            onChange={(event) =>
              onPatch(item.index, {
                allocatedSeconds:
                  event.target.value === '' ? null : Math.round(Number(event.target.value) * 60),
              })
            }
            className={SMALL_NUMBER}
          />
          min
          {item.allocatedSeconds !== null && item.allocatedSeconds !== undefined ? (
            <button
              type="button"
              disabled={shapeLocked}
              onClick={() => onPatch(item.index, { allocatedSeconds: null })}
              className="ml-1 text-fs-11 font-semibold text-forest hover:underline disabled:opacity-50"
              title={`The default for this type and marks is ${defaultSeconds / 60} min`}
            >
              set by you · reset
            </button>
          ) : (
            <span className="ml-1 text-fs-11 text-slate-400">default</span>
          )}
        </label>

        {limit !== null && (
          <label className="flex items-center gap-1 text-xs text-slate-500">
            Answer in ≤
            <input
              type="number"
              min={5}
              step={5}
              value={limit}
              disabled={shapeLocked}
              onChange={(event) =>
                onPatch(item.index, {
                  wordLimit: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              className={SMALL_NUMBER}
            />
            words
          </label>
        )}

        {editable && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="ml-auto text-xs font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Remove
          </button>
        )}
      </div>

      <textarea
        value={item.question}
        disabled={keyLocked}
        onChange={(event) => onPatch(item.index, { question: event.target.value })}
        rows={3}
        placeholder="Question text — maths in $…$, e.g. $x^2 + 5x + 6 = 0$"
        className="mt-3 w-full resize-y rounded-xl border border-line px-3 py-2 text-sm text-ink outline-none focus:border-forest disabled:bg-paper-sunk"
      />
      <MathPreview text={item.question} />

      {type === 'MCQ' && (
        <div className="mt-3 rounded-xl bg-paper-sunk p-3">
          <p className="mb-2 text-fs-11 font-semibold uppercase tracking-wide text-slate-500">
            Options · select the correct choice
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {options.map((option, optionIndex) => (
              <div
                key={`${idPrefix}-${item.index}-${optionIndex}`}
                className="flex items-center gap-2"
              >
                <input
                  type="radio"
                  name={`correct-${idPrefix}-${item.index}`}
                  checked={item.correctOptionIdx === optionIndex}
                  disabled={keyLocked || !option.trim()}
                  onChange={() => onPatch(item.index, { correctOptionIdx: optionIndex })}
                />
                <input
                  value={option}
                  disabled={keyLocked}
                  placeholder={`Option ${String.fromCharCode(65 + optionIndex)}`}
                  onChange={(event) => {
                    const next = [...options];
                    next[optionIndex] = event.target.value;
                    onPatch(item.index, { options: next });
                  }}
                  className="min-w-0 flex-1 rounded-lg border border-line bg-card px-2.5 py-1.5 text-sm outline-none focus:border-forest disabled:bg-paper-sunk"
                />
              </div>
            ))}
          </div>
          {editable && !lockKey && options.length < 6 && (
            <button
              type="button"
              onClick={() => onPatch(item.index, { options: [...options, ''] })}
              disabled={busy}
              className="mt-2 text-xs font-semibold text-forest hover:underline disabled:opacity-50"
            >
              + Add option
            </button>
          )}
        </div>
      )}

      <TeamEditor
        item={item}
        limit={limit}
        editable={editable}
        locked={locked}
        keyLocked={keyLocked}
        generating={generating}
        onPatch={onPatch}
        onGenerate={onGenerate}
      />

      <label className="mt-3 block">
        <span className={`${LABEL} text-slate-500`}>Video · the third-attempt lifeline</span>
        <input
          value={item.videoRef ?? ''}
          disabled={locked}
          placeholder="YouTube link — leave blank for a search on this sub-topic"
          onChange={(event) => onPatch(item.index, { videoRef: event.target.value })}
          className={INPUT}
        />
        {item.videoRef?.trim() && !youTubeEmbedUrl(item.videoRef) && (
          <span className="mt-1 block text-fs-11 text-bin-orange-text">
            Not a link to one video, so it can’t play inside the exam — students get it as a link to
            open instead. Paste a watch or share link to play it in place.
          </span>
        )}
      </label>

      <ConceptTag
        item={item}
        concepts={concepts}
        editable={editable}
        locked={locked}
        tagging={tagging}
        retagLabel={retagLabel}
        onPatch={onPatch}
        onTag={onTag}
      />
    </div>
  );
}

/**
 * TEAM, in place of two loose hint fields. One section with four parts, because
 * the four are read together — by the student as the ladder climbs, and by the
 * teacher on the class report, in this same order.
 */
function TeamEditor({
  item,
  limit,
  editable,
  locked,
  keyLocked,
  generating,
  onPatch,
  onGenerate,
}: {
  item: CustomPaperItem;
  limit: number | null;
  editable: boolean;
  locked: boolean;
  /** The Answer part, held still where the key is (`lockKey`). */
  keyLocked: boolean;
  generating: boolean;
  onPatch: PatchItem;
  onGenerate: (parts: TeamPart[], mode: 'fill' | 'rewrite') => void;
}) {
  const blanks = blankTeamParts(item);
  const marks = item.marks ?? 0;
  const scheme = item.markingScheme ?? [];
  const total = schemeTotal(scheme);
  const answerWords = wordCount(item.answer);

  const main =
    blanks.length === TEAM_PARTS.length
      ? { label: 'Generate TEAM with AI', mode: 'fill' as const, parts: blanks }
      : blanks.length > 0
        ? {
            label: `Fill ${blanks.length} blank part${blanks.length === 1 ? '' : 's'} with AI`,
            mode: 'fill' as const,
            parts: blanks,
          }
        : { label: 'Regenerate all four', mode: 'rewrite' as const, parts: [...TEAM_PARTS] };

  const runMain = () => {
    if (
      main.mode === 'rewrite' &&
      !window.confirm('Replace all four parts with a new AI draft? What you wrote will be lost.')
    ) {
      return;
    }
    onGenerate(main.parts, main.mode);
  };

  const setScheme = (next: CustomPaperItem['markingScheme']) =>
    onPatch(item.index, { markingScheme: next }, 'A');

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-line">
      <div className="flex flex-wrap items-center gap-2 bg-paper-sunk px-3 py-2">
        <span className="text-fs-11 font-bold uppercase tracking-[0.08em] text-forest">TEAM</span>
        <span className="text-fs-11 text-slate-500">
          {TEAM_PARTS.length - blanks.length} of 4 written
        </span>
        {editable && (
          <button
            type="button"
            onClick={runMain}
            disabled={locked || !item.question.trim()}
            title={!item.question.trim() ? 'Write the question first' : undefined}
            className="ml-auto rounded-lg border border-forest bg-card px-3 py-1.5 text-xs font-semibold text-forest hover:bg-forest/5 disabled:opacity-50"
          >
            {generating ? 'Writing…' : main.label}
          </button>
        )}
      </div>

      <TeamPartRow part="T" item={item} editable={editable} locked={locked} onRewrite={onGenerate}>
        {/* One hint per question, kept in `lightHint`; `strongHint` is retired. */}
        <label className="block">
          <span className={`${LABEL} text-amber-600`}>Hint · after a wrong first try</span>
          <textarea
            value={item.lightHint ?? ''}
            disabled={locked}
            rows={2}
            placeholder="Point to the method without giving the answer"
            onChange={(event) => onPatch(item.index, { lightHint: event.target.value }, 'T')}
            className={`${INPUT} resize-y`}
          />
        </label>
      </TeamPartRow>

      <TeamPartRow part="E" item={item} editable={editable} locked={locked} onRewrite={onGenerate}>
        <textarea
          value={item.explanation ?? ''}
          disabled={locked}
          rows={4}
          placeholder={
            'A line of theory, then the worked steps — one per line, e.g.\n1. Divide — 18 = 12 × 1 + 6'
          }
          onChange={(event) => onPatch(item.index, { explanation: event.target.value }, 'E')}
          className={`${INPUT} resize-y`}
        />
        <MathPreview text={item.explanation ?? ''} />
      </TeamPartRow>

      <TeamPartRow
        part="A"
        item={item}
        editable={editable}
        locked={keyLocked}
        onRewrite={onGenerate}
      >
        <textarea
          value={item.answer}
          disabled={keyLocked}
          rows={3}
          placeholder={
            item.type === 'MCQ' ? 'What the correct option says, in full' : 'The model answer'
          }
          onChange={(event) => onPatch(item.index, { answer: event.target.value }, 'A')}
          className={`${INPUT} resize-y`}
        />
        {limit !== null && item.answer.trim() && (
          <p
            className={`mt-1 text-fs-11 ${answerWords > limit ? 'text-bin-orange-text' : 'text-slate-400'}`}
          >
            Model answer {answerWords} words · students get {limit}
            {answerWords > limit && ' — either the answer or the limit needs another look'}
          </p>
        )}
        <MathPreview text={item.answer} />

        <p className={`${LABEL} mt-3 text-slate-500`}>Marking scheme — where the marks are</p>
        <ul className="flex flex-col gap-1.5">
          {scheme.map((s, i) => (
            <li key={i} className="flex items-center gap-2">
              <input
                value={s.step}
                disabled={keyLocked}
                placeholder="What earns the mark"
                onChange={(event) =>
                  setScheme(
                    scheme.map((x, j) => (j === i ? { ...x, step: event.target.value } : x)),
                  )
                }
                className={`${INPUT} flex-1`}
              />
              <input
                type="number"
                min={0.5}
                step={0.5}
                value={s.marks}
                disabled={keyLocked}
                onChange={(event) =>
                  setScheme(
                    scheme.map((x, j) =>
                      j === i ? { ...x, marks: Number(event.target.value) } : x,
                    ),
                  )
                }
                className="w-16 rounded-xl border border-line px-2 py-2 text-center text-sm outline-none focus:border-forest disabled:bg-paper-sunk"
                aria-label="Marks for this step"
              />
              {editable && !keyLocked && (
                <button
                  type="button"
                  disabled={keyLocked}
                  onClick={() => setScheme(scheme.filter((_, j) => j !== i))}
                  className="text-xs font-semibold text-slate-400 hover:text-red-600"
                  aria-label="Remove this step"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-1.5 flex flex-wrap items-center gap-3">
          {editable && !keyLocked && (
            <button
              type="button"
              disabled={keyLocked}
              onClick={() => setScheme([...scheme, { step: '', marks: 1 }])}
              className="text-xs font-semibold text-forest hover:underline disabled:opacity-50"
            >
              + Add step
            </button>
          )}
          {scheme.length > 0 && (
            <span
              className={`text-fs-11 font-semibold ${total === marks ? 'text-bin-green-text' : 'text-bin-orange-text'}`}
            >
              {total === marks
                ? `Totals ${marks} of ${marks} ✓`
                : `Totals ${total} of ${marks} — a scheme has to add up to the question`}
            </span>
          )}
        </div>
      </TeamPartRow>

      <TeamPartRow part="M" item={item} editable={editable} locked={locked} onRewrite={onGenerate}>
        <textarea
          value={(item.mistakes ?? []).join('\n')}
          disabled={locked}
          rows={2}
          placeholder="One per line — what students get wrong here"
          onChange={(event) =>
            onPatch(item.index, { mistakes: event.target.value.split('\n') }, 'M')
          }
          className={`${INPUT} resize-y`}
        />
      </TeamPartRow>
    </div>
  );
}

function TeamPartRow({
  part,
  item,
  editable,
  locked,
  onRewrite,
  children,
}: {
  part: TeamPart;
  item: CustomPaperItem;
  editable: boolean;
  locked: boolean;
  onRewrite: (parts: TeamPart[], mode: 'fill' | 'rewrite') => void;
  children: ReactNode;
}) {
  const drafted = item.aiDrafted?.includes(part);
  return (
    <div className="flex gap-3 border-t border-line px-3 py-3">
      <span
        aria-hidden
        className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-lime-soft text-xs font-bold text-forest"
      >
        {part}
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-xs font-bold text-ink">{TEAM_PART_LABEL[part]}</span>
          {drafted && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-fs-10 font-bold text-amber-700">
              AI draft — review
            </span>
          )}
          {editable && (
            <button
              type="button"
              disabled={locked || !item.question.trim()}
              onClick={() => onRewrite([part], 'rewrite')}
              className="ml-auto text-fs-11 font-semibold text-slate-500 hover:text-forest disabled:opacity-40"
              title={`Rewrite ${TEAM_PART_LABEL[part].toLowerCase()} with AI`}
            >
              ↻ Rewrite
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The concept, as a tag on the syllabus rather than a free-text label: it
 * points at a real sub-topic, shows which topic that is, and can be re-read
 * after the question changes. An untagged label still publishes — as a
 * sub-topic of this paper's own — so nothing is lost by leaving it.
 */
function ConceptTag({
  item,
  concepts,
  editable,
  locked,
  tagging,
  retagLabel,
  onPatch,
  onTag,
}: {
  item: CustomPaperItem;
  concepts: ConceptOption[];
  editable: boolean;
  locked: boolean;
  tagging: boolean;
  retagLabel: string;
  onPatch: PatchItem;
  onTag: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const tagged = concepts.find((c) => c.subtopicId === item.subtopicId);
  const byChapter = useMemo(() => {
    const groups = new Map<string, ConceptOption[]>();
    for (const c of concepts) groups.set(c.chapterName, [...(groups.get(c.chapterName) ?? []), c]);
    return [...groups];
  }, [concepts]);

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="text-fs-11 font-semibold uppercase tracking-wide text-slate-500">
        Concept
      </span>
      {editing ? (
        <select
          autoFocus
          value={item.subtopicId ?? ''}
          disabled={locked}
          onChange={(event) => {
            const picked = concepts.find((c) => c.subtopicId === event.target.value);
            onPatch(item.index, {
              subtopicId: picked?.subtopicId ?? null,
              concept: picked?.name ?? item.concept,
            });
            setEditing(false);
          }}
          onBlur={() => setEditing(false)}
          className="rounded-lg border border-line bg-card px-2 py-1.5 text-xs outline-none focus:border-forest"
        >
          <option value="">Choose a sub-topic</option>
          {byChapter.map(([chapter, list]) => (
            <optgroup key={chapter} label={chapter}>
              {list.map((c) => (
                <option key={c.subtopicId} value={c.subtopicId}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      ) : item.concept?.trim() ? (
        <span className="rounded-full border border-line bg-paper-sunk px-2.5 py-1 text-xs text-ink">
          <strong>{item.concept}</strong>
          {tagged ? (
            <span className="text-slate-500"> · {tagged.chapterName}</span>
          ) : (
            <span className="text-slate-400"> · not on the syllabus yet</span>
          )}
        </span>
      ) : (
        <span className="text-xs italic text-slate-400">Not tagged</span>
      )}
      {editable && !editing && (
        <>
          <button
            type="button"
            disabled={locked || concepts.length === 0}
            onClick={() => setEditing(true)}
            className="text-xs font-semibold text-slate-500 hover:text-forest disabled:opacity-40"
            title="Pick the sub-topic yourself"
          >
            ✎ Change
          </button>
          <button
            type="button"
            disabled={locked || !item.question.trim()}
            onClick={onTag}
            className="text-xs font-semibold text-slate-500 hover:text-forest disabled:opacity-40"
            title="Re-read the question and tag it with the sub-topic it names"
          >
            {tagging ? 'Reading…' : retagLabel}
          </button>
        </>
      )}
    </div>
  );
}
